import React, { useState, useContext, useEffect, useLayoutEffect } from 'react';
import {
  Container,
  useHooks,
  breakpoints,
  Accordion,
  size,
} from '@everlywell/leaves';
import loadable from '@loadable/component';
import Img from 'gatsby-image';
import { sanitize } from 'utils/domHelpers';
import ewAnalytics from 'utils/analytics';
import { ANALYTICS } from 'utils/constants/analytics';
import { ImageType, CTA } from 'utils/types';
import { generateKey } from 'utils/helpers';

import { ProductInfoContext } from '../../../templates/pdp';
import MeasuredItemsModal from 'components/MeasuredItemsModal';

import * as S from './styles';

import DesktopMenu from './DesktopMenu';

type SectionType = {
  title: string;
  slug: string;
  headline: string;
  subheadline?: string;
  copy?: string;
  desktopImages?: Array<ImageType>;
  mobileImages?: Array<ImageType>;
  modalButtonText?: string;
  modalTitle?: string;
  link?: string;
  cta?: CTA;
  listOfContent?: Array<{
    title: string;
    description: string;
    image: string;
    cta?: CTA;
  }>;
};
export type menuItem = {
  menuTitle: string;
  slug: string;
};
export interface DetailsSectionProps {
  content: SectionType[];
}

export const DetailsSection: React.FC<DetailsSectionProps> = ({ content }) => {
  const windowSize = useHooks.useWindowSize();
  const isMobile =
    !!windowSize.width && windowSize.width < breakpoints.forTabletHorizontalUp;
  const [activeIndex, setActiveIndex] = useState<number>(0);
  const { markerList } = useContext(ProductInfoContext);
  const RelatedSymptomsContent: React.FC<SectionType> = section => (
    <div>
      <S.SectionHeadline>{section.headline}</S.SectionHeadline>
      <S.SectionCopy>{section.copy}</S.SectionCopy>
      <S.SymptomContainer>
        {section.listOfContent?.map(symptom => {
          const formattedSymptoms = symptom.description.split('\n');
          return (
            <S.SymptomWrapper key={generateKey()}>
              <S.SymptomIcon aria-hidden="true" src={symptom.image} />
              <S.SymptomList>
                <li>
                  <S.SymptomTitle>{symptom.title}</S.SymptomTitle>
                </li>
                {formattedSymptoms.map(symptom => (
                  <S.Symptom key={generateKey()}>{symptom}</S.Symptom>
                ))}
              </S.SymptomList>
            </S.SymptomWrapper>
          );
        })}
      </S.SymptomContainer>
    </div>
  );

  const WhatsMeasuredContent: React.FC<SectionType> = section => {
    const [open, setOpen] = useState(false);
    const handleModalClick = (title: string) => {
      setOpen(true);
      ewAnalytics.track({
        event: ANALYTICS.EVENTS.CLICKED_BUTTON,
        data: {
          label: `Open Modal - ${title}`,
        },
      });
    };
    const formattedListOfItems = section.listOfContent?.map(item => {
      const formattedItemNames = item.description?.split('\n');
      return {
        image: item.image || '',
        title: item.title,
        items: formattedItemNames,
      };
    });

    return (
      <div>
        <S.SectionHeadline>{section.headline}</S.SectionHeadline>
        <S.SectionSubheadline>{section.subheadline}</S.SectionSubheadline>
        <S.SectionCopy>{section.copy}</S.SectionCopy>
        {section.modalButtonText ? (
          <>
            <S.TertiaryLink
              role="button"
              onClick={() => {
                handleModalClick(section.modalButtonText || '');
              }}
              data-testid="whatsMeasuredModalTrigger"
            >
              {section.modalButtonText}
            </S.TertiaryLink>
            <MeasuredItemsModal
              open={open}
              openHandler={setOpen}
              listOfItems={formattedListOfItems || []}
              modalTitle={section.modalTitle || ''}
            />
          </>
        ) : (
          <S.MarkerListContainer data-testid="whatsMeasuredMarkers">
            {markerList.markers?.map(marker => (
              <S.MarkerWrapper key={marker.title}>
                <S.MarkerName>{marker.title}</S.MarkerName>
                <S.MarkerDescription>
                  <span
                    dangerouslySetInnerHTML={{
                      __html: sanitize(marker.description?.description),
                    }}
                  />
                </S.MarkerDescription>
              </S.MarkerWrapper>
            ))}
          </S.MarkerListContainer>
        )}
      </div>
    );
  };

  const CollectionMethodsContent: React.FC<SectionType> = section => {
    const images = isMobile ? section.mobileImages : section.desktopImages;

    return (
      <div>
        <S.SectionHeadline>{section.headline}</S.SectionHeadline>
        <div>
          {images
            ? images?.map(imageData => (
                <S.SectionContentContainer key={generateKey()}>
                  <Img
                    fluid={imageData.fluid}
                    style={{ width: '75%' }}
                    alt={imageData.description}
                  />
                </S.SectionContentContainer>
              ))
            : null}
          {section.copy && section.link ? (
            <S.SectionLink
              target={isMobile ? '_self' : '_blank'}
              href={section.link}
            >
              {section.copy}
              <S.NewWindowIcon />
            </S.SectionLink>
          ) : null}
          {section.listOfContent
            ? section.listOfContent.map(({ cta }) =>
                cta ? (
                  <S.Centered>
                    <S.CTA
                      target={isMobile ? '_self' : '_blank'}
                      href={cta.url}
                    >
                      {cta.text}
                      <S.NewWindowIcon />
                    </S.CTA>
                  </S.Centered>
                ) : null,
              )
            : null}
        </div>
      </div>
    );
  };

  const WhatsInTheBoxContent: React.FC<SectionType> = section => {
    const images = isMobile ? section.mobileImages : section.desktopImages;
    const whatsIncludedItems = section.listOfContent
      ? section.listOfContent
      : [];

    return (
      <div>
        <S.SectionHeadline>{section.headline}</S.SectionHeadline>
        <div>
          {images
            ? images?.map(imageData => (
                <S.SectionContentContainer key={generateKey()}>
                  <Img
                    fluid={imageData.fluid}
                    style={{ width: '100%' }}
                    alt={imageData.description}
                  />
                </S.SectionContentContainer>
              ))
            : null}
          {whatsIncludedItems.length ? (
            <S.SectionList>
              {whatsIncludedItems?.map(item => (
                <S.SectionListItem key={generateKey()}>
                  {item.title}
                </S.SectionListItem>
              ))}
            </S.SectionList>
          ) : null}
        </div>
      </div>
    );
  };
  const DefaultContent: React.FC<SectionType> = ({
    headline,
    subheadline,
    copy,
    listOfContent,
    cta,
  }) => {
    return (
      <div>
        <S.SectionHeadline>{headline}</S.SectionHeadline>
        <S.SectionSubheadline>{subheadline}</S.SectionSubheadline>
        <S.SectionCopy>{copy}</S.SectionCopy>
        {cta ? (
          <S.TertiaryLink target="_blank" href={cta.url}>
            {cta.text}
          </S.TertiaryLink>
        ) : null}
        {listOfContent
          ? listOfContent.map(({ cta }) =>
              cta ? (
                <S.TertiaryLink href={cta.url}>{cta.text}</S.TertiaryLink>
              ) : null,
            )
          : null}
      </div>
    );
  };
  //create menu and content component map
  const menuArray: menuItem[] = [];
  const contentArray = content.map(section => {
    menuArray.push({ menuTitle: section.title, slug: section.slug });
    switch (section.slug) {
      case 'relatedSymptoms':
        return <RelatedSymptomsContent {...section} />;
      case 'whatsMeasured':
        return <WhatsMeasuredContent {...section} />;
      case 'collectionMethods':
        return <CollectionMethodsContent {...section} />;
      case 'whatsInTheBox':
        return <WhatsInTheBoxContent {...section} />;
      default:
        return <DefaultContent {...section} />;
    }
  });

  const handleClick = (sectionTitle: string) => {
    ewAnalytics.track({
      event: ANALYTICS.EVENTS.CLICKED_BUTTON,
      data: {
        label: `PDP Details Section - ${sectionTitle}`,
      },
    });
  };

  const [openChild, setOpenChild] = useState(``);

  useEffect(() => {
    window.addEventListener('hashchange', handleFragment, false);
    return () => {
      window.removeEventListener('hashchange', handleFragment, false);
    };
  }, []);

  // runs only one time on initial mount - set initial openChild if there is one
  useEffect(() => {
    const hash = window.location.hash.substring(1);
    if (hash) {
      setOpenChild(hash);
    }
  }, []);

  useLayoutEffect(() => {
    // isMobile variable doesn't seem to react to changes in window after load?
    // TODO: This isn't great, but it works for now. There should be a better way to query for `isMobile`
    const isDesktop = document.getElementById('details-desktop');

    if (openChild) {
      if (isDesktop) {
        scrollToDetailsSection();
        setActiveDetailsDesktop(openChild);
      } else {
        scrollToAccordionHash(openChild);
      }
    }
  }, [openChild]);

  const handleFragment = () => {
    const hash = window.location.hash.substring(1);
    if (hash) {
      resetLocationHash();
      setOpenChild(hash);
    }
  };
  const setActiveDetailsDesktop = (hash: string) => {
    const detailsTab = document.querySelector(
      `[data-slug="${hash}" ]`,
    ) as HTMLElement;

    if (detailsTab) {
      const index = parseInt(detailsTab.dataset.index || '');
      setActiveIndex(index);
    }
  };
  const scrollToAccordionHash = (hash: string) => {
    const activeAccordion = document.querySelector(
      `[id$="${hash}" ]`,
    ) as HTMLElement;

    if (activeAccordion) scrollToElement(activeAccordion, { offset: 125 });
  };

  const scrollToDetailsSection = () => {
    const detailsSection = document.getElementById('details-desktop');
    if (detailsSection) scrollToElement(detailsSection);
  };

  const scrollToElement = (el: HTMLElement, options = { offset: 250 }) => {
    // Generalized scrollTo that accounts for our header's height.
    const top = el.getBoundingClientRect().y || options.offset;
    const yPos = window.scrollY; // accounts for the header width
    window.scrollTo(0, top + yPos - options.offset);
  };
  const resetLocationHash = () => {
    // Reset hash so we can click the same link multiple times.
    // Remove fragment as much as it can go without adding an entry in browser history:
    window.location.replace('#');
    // slice off the remaining '#'
    if (typeof window.history.replaceState == 'function') {
      history.replaceState({}, '', window.location.href.slice(0, -1));
    }
    setOpenChild(``);
  };
  return (
    <S.Section data-testid="detailsSection">
      <Container>
        {isMobile ? (
          <Accordion.Wrapper
            openChild={openChild}
            scrollToOpenedContent
            allowMultipleOpened
          >
            {menuArray.map(({ menuTitle, slug }, i: number) => (
              <Accordion.Item key={menuTitle} id={`details-${slug}`}>
                <S.AccordionHeading onClick={() => handleClick(menuTitle)}>
                  {menuTitle}
                </S.AccordionHeading>
                <Accordion.Child>{contentArray[i]}</Accordion.Child>
                <S.Divider />
              </Accordion.Item>
            ))}
          </Accordion.Wrapper>
        ) : (
          <DesktopMenu
            menuArray={menuArray}
            contentArray={contentArray}
            activeIndex={activeIndex}
            setActiveIndex={setActiveIndex}
          />
        )}
      </Container>
    </S.Section>
  );
};

export default DetailsSection;
